import { FormControl, FormHelperText, InputLabel, Select, SelectProps } from '@mui/material'
import { Fragment, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { FormViewerFieldMetadata } from '@omnea-app/fe-form-viewer/types'
import { useUniqueId } from '@omnea-app/frontend/utils/hooks/useUniqueId'

export interface LabeledSelectType<T> extends Omit<SelectProps<T>, 'variant'> {
  label?: ReactNode
  withLabelWrapper?: boolean
  required?: boolean
  placeholder?: string
  helperText?: ReactNode
  isLoading?: boolean
  fieldMetadata?: FormViewerFieldMetadata
  fullWidth?: boolean
}

export function LabeledSelect<T>(props: LabeledSelectType<T>) {
  const {
    label,
    required,
    helperText,
    withLabelWrapper = true,
    sx,
    isLoading = false,
    fieldMetadata,
    fullWidth = true,
    disabled,
    ...otherProps
  } = props
  const { t } = useTranslation('common')
  const id = useUniqueId()

  const LabelWrapper = withLabelWrapper ? InputLabel : Fragment

  const placeholder = isLoading ? `${t('loading')}...` : props.placeholder

  const isDisabled = isLoading || disabled

  return (
    <FormControl fullWidth={fullWidth}>
      {label && (
        <LabelWrapper htmlFor={id} required={required}>
          {label}
        </LabelWrapper>
      )}
      <Select<T>
        inputProps={{
          id,
        }}
        data-is-ai-prefilled={fieldMetadata?.isAiPrefilled}
        notched={false}
        sx={{
          '& .MuiSelect-select .notranslate::after': placeholder
            ? {
                content: `"${placeholder}"`,
                opacity: 0.42,
              }
            : {},
          ...sx,
        }}
        {...otherProps}
        disabled={isDisabled}
      >
        {props.children}
      </Select>
      {helperText && <FormHelperText error={otherProps.error}>{helperText}</FormHelperText>}
    </FormControl>
  )
}
